import React, { useCallback, useEffect, useState } from 'react';
import {
  Drawer,
  Descriptions,
  Tag,
  Collapse,
  Alert,
  Skeleton,
  Button,
  Space,
  Typography,
  message,
} from 'antd';
import { Link } from 'react-router-dom';
import { CopyOutlined, LinkOutlined, WarningOutlined } from '@ant-design/icons';
import {
  fetchTransactionLogDetail,
  fetchTransactionLogEvents,
  type TransactionLogDetail,
  type TransactionLogEvent,
} from '../../../services/transactionLogsService';
import { formatVnDateTime } from '../../../utils/vnDateTime';
import { formatInternalStatusLabel } from '../../../utils/transactionLogStatusLabels';

const { Text } = Typography;

const formatVnd = (v: number | null | undefined): string => {
  if (v == null) return '–';
  return (
    new Intl.NumberFormat('vi-VN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(v) + ' đ'
  );
};

const formatDateTime = (v: string | null | undefined): string => {
  if (!v) return '–';
  const formatted = formatVnDateTime(v);
  return formatted === '—' ? '–' : formatted;
};

const STATUS_COLORS: Record<string, string> = {
  SUCCEEDED: 'green',
  CAPTURED: 'green',
  COMPLETED: 'green',
  PENDING: 'gold',
  PENDING_APPROVAL: 'gold',
  FAILED: 'red',
  CANCELLED: 'default',
  EXPIRED: 'default',
};

const copyToClipboard = (text: string, label: string) => {
  navigator.clipboard.writeText(text).then(
    () => message.success(`Đã copy ${label}`),
    () => message.error('Không thể copy')
  );
};

const JsonViewer: React.FC<{ data: unknown; label?: string }> = ({
  data,
  label,
}) => {
  const str =
    data == null
      ? 'null'
      : typeof data === 'object'
      ? JSON.stringify(data, null, 2)
      : String(data);
  return (
    <pre
      style={{
        background: '#f5f5f5',
        padding: 12,
        borderRadius: 8,
        fontSize: 12,
        overflow: 'auto',
        maxHeight: 400,
      }}
    >
      {str}
    </pre>
  );
};

interface TransactionLogDetailDrawerProps {
  id: number | null;
  open: boolean;
  onClose: () => void;
  onRefresh?: () => void;
}

export const TransactionLogDetailDrawer: React.FC<
  TransactionLogDetailDrawerProps
> = ({ id, open, onClose, onRefresh }) => {
  const [detail, setDetail] = useState<TransactionLogDetail | null>(null);
  const [events, setEvents] = useState<TransactionLogEvent[]>([]);
  const [loading, setLoading] = useState(false);

  const loadDetail = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [detailRes, eventsRes] = await Promise.all([
        fetchTransactionLogDetail(id),
        fetchTransactionLogEvents(id),
      ]);
      if (detailRes.success && detailRes.data) setDetail(detailRes.data);
      else setDetail(null);
      if (eventsRes.success && eventsRes.data) setEvents(eventsRes.data);
      else setEvents([]);
    } catch {
      setDetail(null);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (open && id) loadDetail();
    else {
      setDetail(null);
      setEvents([]);
    }
  }, [open, id, loadDetail]);

  if (!id) return null;

  return (
    <Drawer
      title={`Transaction #${id}`}
      placement="right"
      width={720}
      onClose={onClose}
      open={open}
      extra={
        <Space>
          <Button
            type="link"
            icon={<CopyOutlined />}
            onClick={() => copyToClipboard(String(id), 'Transaction ID')}
          >
            Copy ID
          </Button>
          {detail?.reference_id && (
            <Button
              type="link"
              icon={<CopyOutlined />}
              onClick={() =>
                copyToClipboard(detail.reference_id!, 'Reference ID')
              }
            >
              Copy Ref
            </Button>
          )}
          {detail?.order_id && (
            <Link to={`/orders/${detail.order_id}`}>
              <Button type="primary" icon={<LinkOutlined />}>
                Mở đơn hàng
              </Button>
            </Link>
          )}
        </Space>
      }
    >
      {loading ? (
        <Skeleton active paragraph={{ rows: 12 }} />
      ) : !detail ? (
        <Alert type="warning" message="Không tìm thấy giao dịch" />
      ) : (
        <>
          {detail.riskFlags && detail.riskFlags.length > 0 && (
            <Alert
              type="warning"
              icon={<WarningOutlined />}
              message="Cảnh báo điều tra"
              description={
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {detail.riskFlags.map(r => (
                    <li key={r.type}>{r.message}</li>
                  ))}
                </ul>
              }
              style={{ marginBottom: 16 }}
            />
          )}

          <Descriptions
            title="Tổng quan"
            column={1}
            bordered
            size="small"
            style={{ marginBottom: 16 }}
          >
            <Descriptions.Item label="Transaction ID">
              {detail.id}
            </Descriptions.Item>
            <Descriptions.Item label="Order">
              {detail.order_id ? (
                <Link to={`/orders/${detail.order_id}`}>
                  {detail.order_code ? `#${detail.order_code}` : '–'}
                </Link>
              ) : (
                '–'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Merchant">
              {detail.restaurant_id ? (
                <Link to={`/merchants/${detail.restaurant_id}`}>
                  {detail.merchant_name || `#${detail.restaurant_id}`}
                </Link>
              ) : (
                detail.merchant_name || '–'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Customer">
              {detail.customer_name || detail.customer_phone || '–'}
            </Descriptions.Item>
            <Descriptions.Item label="Số tiền">
              {formatVnd(detail.amount)}
            </Descriptions.Item>
            <Descriptions.Item label="Platform fee">
              {formatVnd(detail.platform_fee_amount)}
            </Descriptions.Item>
            <Descriptions.Item label="Merchant amount">
              {formatVnd(detail.merchant_amount)}
            </Descriptions.Item>
            <Descriptions.Item label="Currency">
              {detail.currency || 'VND'}
            </Descriptions.Item>
            <Descriptions.Item label="Payment method">
              {detail.payment_method || '–'}
            </Descriptions.Item>
            <Descriptions.Item label="Transaction type">
              <Tag
                color={detail.transaction_type === 'refund' ? 'orange' : 'blue'}
              >
                {detail.transaction_type === 'refund'
                  ? 'Hoàn tiền'
                  : 'Thanh toán'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Trạng thái nội bộ">
              <Tag
                color={STATUS_COLORS[detail.internal_status || ''] || 'default'}
              >
                {formatInternalStatusLabel(
                  detail.internal_status,
                  detail.transaction_type
                )}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Gateway status">
              <Tag
                color={STATUS_COLORS[detail.gateway_status || ''] || 'default'}
              >
                {detail.gateway_status || '–'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Provider">
              {detail.provider || '–'}
            </Descriptions.Item>
            <Descriptions.Item label="Created at">
              {formatDateTime(detail.created_at)}
            </Descriptions.Item>
            <Descriptions.Item label="Updated at">
              {formatDateTime(detail.updated_at)}
            </Descriptions.Item>
          </Descriptions>

          <Descriptions
            title="Provider Information"
            column={1}
            bordered
            size="small"
            style={{ marginBottom: 16 }}
          >
            <Descriptions.Item label="Provider Transaction ID">
              {detail.provider_transaction_id || '–'}
            </Descriptions.Item>
            <Descriptions.Item label="Reference ID">
              {detail.reference_id || '–'}
            </Descriptions.Item>
            <Descriptions.Item label="Channel code">
              {detail.channel_code || '–'}
            </Descriptions.Item>
            <Descriptions.Item label="Refer ID (AppotaPay)">
              {detail.sub_account_id || '–'}
            </Descriptions.Item>
            <Descriptions.Item label="Callback URL">
              {detail.callback_url ? (
                <Text copyable style={{ wordBreak: 'break-all' }}>
                  {detail.callback_url}
                </Text>
              ) : (
                '–'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Checkout URL">
              {detail.checkout_url ? (
                <a
                  href={detail.checkout_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Mở link
                </a>
              ) : (
                '–'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Deeplink">
              {detail.deeplink ? 'Có' : '–'}
            </Descriptions.Item>
            <Descriptions.Item label="QR String">
              {detail.qr_string ? 'Có' : '–'}
            </Descriptions.Item>
          </Descriptions>

          <Descriptions
            title="Order Linkage"
            column={1}
            bordered
            size="small"
            style={{ marginBottom: 16 }}
          >
            <Descriptions.Item label="Order status">
              {detail.order_status || '–'}
            </Descriptions.Item>
            <Descriptions.Item label="Order total">
              {formatVnd(detail.order_total)}
            </Descriptions.Item>
            <Descriptions.Item label="Delivery fee">
              {formatVnd(detail.order_delivery_fee)}
            </Descriptions.Item>
            <Descriptions.Item label="Discount">
              {formatVnd(detail.order_discount)}
            </Descriptions.Item>
            <Descriptions.Item label="Amount match">
              {detail.amountMatch === true ? (
                <Tag color="green">Matched</Tag>
              ) : detail.amountMatch === false ? (
                <Tag color="red">Mismatched</Tag>
              ) : (
                '–'
              )}
            </Descriptions.Item>
          </Descriptions>

          {(detail.error_code || detail.error_message) && (
            <Descriptions
              title="Failure Analysis"
              column={1}
              bordered
              size="small"
              style={{ marginBottom: 16 }}
            >
              <Descriptions.Item label="Error code">
                {detail.error_code || '–'}
              </Descriptions.Item>
              <Descriptions.Item label="Error message">
                {detail.error_message || '–'}
              </Descriptions.Item>
            </Descriptions>
          )}

          <Collapse
            items={[
              {
                key: 'request',
                label: 'Request Payload',
                children: <JsonViewer data={detail.request_payload} />,
              },
              {
                key: 'response',
                label: 'Response Payload',
                children: <JsonViewer data={detail.response_payload} />,
              },
              {
                key: 'webhook',
                label: 'Webhook Payload',
                children: <JsonViewer data={detail.webhook_payload} />,
              },
              {
                key: 'metadata',
                label: 'Metadata',
                children: <JsonViewer data={detail.metadata} />,
              },
            ]}
            style={{ marginBottom: 16 }}
          />

          {events.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <h4>Event History</h4>
              {events.map(ev => (
                <div
                  key={ev.id}
                  style={{
                    padding: 12,
                    background: '#fafafa',
                    borderRadius: 8,
                    marginBottom: 8,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: 4,
                    }}
                  >
                    <Tag>{ev.event_type}</Tag>
                    <Text type="secondary">
                      {formatDateTime(ev.created_at)}
                    </Text>
                  </div>
                  {ev.event_message ? <div>{ev.event_message}</div> : null}
                  {ev.actor_type ? (
                    <Text type="secondary">Actor: {ev.actor_type}</Text>
                  ) : null}
                  {ev.payload ? (
                    <Collapse
                      size="small"
                      items={[
                        {
                          key: 'payload',
                          label: 'Payload',
                          children: <JsonViewer data={ev.payload} />,
                        },
                      ]}
                    />
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </Drawer>
  );
};
